import { appConfig } from "./config";
import { escapeCsvField, formatDateTimeISO, formatTime } from "./utils";
import markerDefault from '../assets/marker_grey.png';
import markerPurple from '../assets/marker_purple.png';
import markerRed from '../assets/marker_red.png';
import markerYellow from '../assets/marker_yellow.png';
import markerGreen from '../assets/marker_green.png';
import markerBlue from '../assets/marker_blue.png';
import { EnumDeviceSearchType, IEventSearchParams } from "./models";
import dayjs from "dayjs";

export function formatRecentEvents(events: any[] | undefined): string {
  if (!events || events.length === 0) {
    return `No events received in past ${appConfig.settings.fetchRecentEventsMins} minutes.`;
  }
  const count = events.length;
  const testPayloadCount = appConfig.settings.testPayloadCount;
  return events.map((event, index) => {
    const status = appConfig.getRssiStatus(event.Device.RSSI);
    const statusText = status ? `${status.text} (${event.Device.RSSI})` : 'Unknown';
    const num = String(count - index).padStart(2, '0');
    let testPayload = '';
    const remainingPackets = event['Remaining Test Packets'];
    if (remainingPackets) {
      testPayload = ` | RSSI Test Payload: ${testPayloadCount-remainingPackets+1}/${testPayloadCount}`;
    }
    return `[${num}] ${formatTime( event.EventOccurredAt)} | RSSI: ${statusText} ${testPayload}`;
  }).join('\n');
}

export function isLevelSense(deviceTypeName: string): boolean {
  return !!deviceTypeName && deviceTypeName.startsWith('LevelSense');
}

export function validateLevelDistances(emptyDistance: string, fullDistance: string): boolean {
  if (emptyDistance.length === 0 && fullDistance.length === 0) return true;
  const empty = parseFloat(emptyDistance);
  const full = parseFloat(fullDistance);
  return !isNaN(empty) && !isNaN(full) && empty > full;
}

export function parseSerialNumbers(serialNumbers: string): string[] {
  if (!serialNumbers) return [];
  const sns = serialNumbers
    .split(/["';,:\s\r\n]+/) // Split by comma, semicolon, colon, whitespace, or line break
    .map(sn => sn.trim().replace(/^\[|\]$/g, ''))
    .filter(sn => sn.length > 0); // Filter out empty strings
  return Array.from(new Set(sns));  // Remove duplicates
}

export function getValidSerialNumbers(serialNumbers: string[]): string[] {
  if (!serialNumbers || serialNumbers.length === 0) return [];
  // Filter out empty strings and non-numeric serial numbers
  return serialNumbers.filter(sn => sn.length === 8 && /^\d+$/.test(sn));
}

export function convertDevicesToString(devices: any[]): string {
  return "Device Serial,Network ID,Unsubscribed from Network,Removed from Losant\n" + devices.map(device => {
    const unsubscribeMsg = device.NetworkUnsubscribeOk ? 'Unsubscribed' : device.NetworkMsg ?? device.PipMsg;
    const removeMsg = device.LosantRemoveOk ? 'Removed' : device.LosantMsg ?? device.PipMsg;
    return `[${device.SerialNumber}],[${device.NetworkId}],${unsubscribeMsg},${removeMsg}`;
  }).join('\n');
}

export function getFileNameBasedOnTime(prefix: string): string {
  const now = new Date();
  // Generate a file name like "PiP_Deactivation_Results_2025_06_17_10_48_08.csv".
  // Use the 'sv-SE' locale to ensure consistent formatting (YYYY-MM-DD HH:mm:ss).
  // The 'en-NZ' locale would produce a format like "16/06/2025, 2:53:12 pm", which is less suitable for filenames.
  const nzTime = now.toLocaleString('sv-SE', { timeZone: 'Pacific/Auckland' });
  return prefix + nzTime.replace(/[-: ]/g, '_') + '.csv';
}

export function isLevelSenseDevice(device: any): boolean {
  return device?.DeviceTypeName?.startsWith('LevelSense') ?? false;
}

export function isPeopleSenseDevice(device: any): boolean {
  return device?.DeviceTypeName?.startsWith('PeopleSense') ?? false;
}

export function getDeviceName(device: any): string {
  return device?.InstallationData?.AdditionalData?.Name || '';
}

export function getDeviceAddress(device: any): string {
  return device?.InstallationData?.AdditionalData?.Address || '';
}

export function getEventChartTitle(device: any): string {
    const parts = [device.SerialNumber];
    const deviceName = getDeviceName(device);
    const deviceAddress = getDeviceAddress(device);
    if (deviceName) parts.push(deviceName);
    if (deviceAddress) parts.push(deviceAddress);
    return parts.join(' - ');
}

export function convertEvents(devices: any[], events: any[], needSplitEvents = false): any[] {
  if (!events || events.length === 0) return [];

  const devicesMap = devices.reduce((map, device) => {
    map[device.SerialNumber] = device;
    return map;
  }, {} as { [SerialNumber: string]: any });

  const convertedEvents: any[] = [];
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const eventTime = event['EventOccurredAt'];
    const totalCount = event['Total Count'];
    const countGroups = event['Count Groups'];
    const countGroupCount = Array.isArray(countGroups) ? countGroups.length : 0;
    const device = devicesMap[event.Device.SerialNumber];
    const converted = {
      key: event.EventId,
      eventId: event.EventId,
      eventTime: eventTime,
      serialNumber: event.Device.SerialNumber,
      deviceName: getDeviceName(device),
      deviceType: event.Device.Type,
      magSwitchFlag: event['Mag Switch'],
      motionFlag: event['Motion Flag'],
      tiltedFlag: event['Tilted Flag'],
      tiltAngle: event['Tilt Angle'],
      laserDistance: event['Laser Distance'],
      // Ultrasonic distance of 0 is actually an error, so ignore it
      ultrasonicDistance: event['Ultrasonic Distance'] > 0 ? event['Ultrasonic Distance'] : undefined,
      level: event['Level'],
      totalCount,
      count: countGroupCount > 0 ? countGroups.join(';') : undefined,
      statusDetails: event.StatusDetails,
      rssi: event.Device.RSSI,
      temperature: event.Temperature,
      voltage: event['Battery Voltage'],
    };
    if (needSplitEvents && isPeopleSenseDevice(device) && totalCount > 0 && countGroupCount > 0) {
      // Need to split the original event into multiple events based on the count groups, and adjust the event time for each split event accordingly.
      const currEventTime = new Date(eventTime).getTime();
      // TODO: We will need to use the configured "Sensor Sample Period" against the device to correctly calculate the count periods.
      const interval = 3600000 / countGroupCount; // 3600000 is 1 hour in milliseconds
      let preCount = 0;
      // Example: [6, 2, 2, 1, 7, 14], where the rightmost value represents the most recent count group, like 14.
      // Since the current event time corresponds to the most recent count group, we must reverse the
      // count groups to correctly compute the event time for each split event.
      let counts = [...countGroups].reverse();
      for (let j = 0; j < counts.length; j++) {
        const currCount = counts[j];
        const splitEvent = {
          ...converted,
          key: `${event.EventId}_${j}`,
          totalCount: totalCount - preCount,
          count: currCount,
          countIntervalMins: interval / 60000,
          eventTime: dayjs(currEventTime - j * interval).format('YYYY-MM-DDTHH:mm:ssZ'),
        };
        convertedEvents.push(splitEvent);
        preCount += currCount;
      }
    } else {
      // No need to split, just push the converted event as is.
      convertedEvents.push(converted);
    }
  }

  return convertedEvents;
}

export function convertEventsToCSV(
  events: any[],
  isSingleDevice: boolean,
  isLevelSense: boolean,
  isPeopleSense: boolean,
): string {
  // LevelSense:  Event Time,Device Serial,Name,Device Type,MagSwitch Flag,
  //              Motion Flag,Tilted Flag,Tilt Angle,Laser Distance,Ultrasonic Distance,Level,
  //              RSSI,Temperature (C),Voltage (V),Status,Event ID
  // PeopleSense: Event Time,Device Serial,Name,Device Type,MagSwitch Flag,
  //              Total Count,Count,
  //              RSSI,Temperature (C),Voltage (V),Status,Event ID

  // CSV Headers
  const titleFields = ["Event Time", "Device Serial", "Name", "Device Type", "MagSwitch Flag"]; // Common fields
  const levelSenseTitles = ["Motion Flag", "Tilted Flag", "Tilt Angle", "Laser Distance", "Ultrasonic Distance", "Level"];
  const peopleSenseTitles = ["Total Count", "Count"];
  const followingTitles = ["RSSI", "Temperature (C)", "Voltage (V)", "Status", "Event ID"]; // Common fields

  if (isSingleDevice) { // Export events for a single device
    if (isLevelSense) {
      titleFields.push(...levelSenseTitles);
    } else if (isPeopleSense) {
      titleFields.push(...peopleSenseTitles);
    }
  } else { // Export events for multiple devices
    titleFields.push(...levelSenseTitles, ...peopleSenseTitles);
  }
  titleFields.push(...followingTitles);

  // CSV Values
  const titleRow = titleFields.join(',');
  const valueRows = events.map(event => {
    const columns = [
      formatDateTimeISO(event.eventTime),
      event.serialNumber ?? '',
      escapeCsvField(event.deviceName ?? ''),
      event.deviceType ?? '',
      event.magSwitchFlag ?? '',
    ]; // Common fields
    const levelSenseFields = [
      event.motionFlag ?? '',
      event.tiltedFlag ?? '',
      event.tiltAngle ?? '',
      event.laserDistance ?? '',
      event.ultrasonicDistance ?? '',
      event.level ?? '',
    ];
    const peopleSenseFields = [
      event.totalCount ?? '',
      event.count ?? '',
    ];
    const followingFields = [
      event.rssi ?? '',
      event.temperature ?? '',
      event.voltage ?? '',
      event.statusDetails ? Object.entries(event.statusDetails).map((x: any) => x.Detail).join(';') : '',
      event.eventId ?? '',
    ]; // Common fields

    if (isSingleDevice) { // Export events for a single device
      if (isLevelSense) {
        columns.push(...levelSenseFields);
      } else if (isPeopleSense) {
        columns.push(...peopleSenseFields);
      }
    } else { // Export events for multiple devices
      columns.push(...levelSenseFields, ...peopleSenseFields);
    }
    columns.push(...followingFields);

    return columns.join(',');
  });

  return titleRow + "\n" + valueRows.join('\n');
}

export function getEventFileName(searchParams: IEventSearchParams, customers: any, firstEvent: any): string {
  let prefix = 'Events_';
  if (searchParams.searchType === EnumDeviceSearchType.customerName) {
    prefix += `${searchParams.keywords}`;
  } else if (searchParams.searchType === EnumDeviceSearchType.customerId) {
    const customer = customers.find((c: any) => c.CustomerId === searchParams.keywords);
    if (customer) {
      prefix += `${customer.Name}`;
    } else {
      prefix += `CustomerId_${searchParams.keywords}`;
    }
  } else if (searchParams.searchType === EnumDeviceSearchType.deviceIdSerial) {
    if (firstEvent && firstEvent.Device && firstEvent.Device.SerialNumber) {
      prefix += `${firstEvent.Device.SerialNumber}`;
    } else {
      prefix += `${searchParams.keywords}`;
    }
  } else if (searchParams.searchType === EnumDeviceSearchType.networkId) {
    if (firstEvent && firstEvent.Device && firstEvent.Device.SerialNumber) {
      prefix += `${firstEvent.Device.SerialNumber}`;
    } else {
      prefix += `NetworkID_${searchParams.keywords}`;
    }
  }
  if (searchParams.startTime && searchParams.endTime) {
    return `${prefix}_${searchParams.startTime}_${searchParams.endTime}.csv`;
  }
  return `${prefix}.csv`;
}

export function getDeviceMarkerState(eventsMap: { [deviceSerial: string]: any }, deviceSerial: string, isPeopleSense: boolean): {
  icon: string,
  level: number | null,
  ultrasonicDistance: number | null,
} {
  if (!eventsMap || !deviceSerial) return { icon: markerDefault, level: null, ultrasonicDistance: null };
  const event = eventsMap[deviceSerial];
  if (!event) return { icon: markerDefault, level: null, ultrasonicDistance: null };

  if (isPeopleSense) { // PeopleSense device
    return { icon: markerBlue, level: null, ultrasonicDistance: null };
  }

  // LevelSense device
  if (event.Level === undefined) return { icon: markerPurple, level: null, ultrasonicDistance: event['Ultrasonic Distance'] || null };
  if (event.Level >= 75) return { icon: markerRed, level: event.Level, ultrasonicDistance: event['Ultrasonic Distance'] || null };
  if (event.Level >= 50) return { icon: markerYellow, level: event.Level, ultrasonicDistance: event['Ultrasonic Distance'] || null };
  return { icon: markerGreen, level: event.Level, ultrasonicDistance: event['Ultrasonic Distance'] || null };
}

export function findDevice(searchType: EnumDeviceSearchType, keywords: string | undefined, devices: any[]): any {
  if (!keywords || !devices || devices.length === 0) return null;
  let device: any = null;
  if (searchType === EnumDeviceSearchType.deviceIdSerial) {
    device = devices.find((d: any) => d.DeviceId === keywords || d.SerialNumber === keywords);
  } else if (searchType === EnumDeviceSearchType.networkId) {
    device = devices.find((d: any) => d.NetworkId === keywords);
  }
  return device;
}